import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { AppConfig } from 'src/app/app.config';
import { catchError, map } from 'rxjs/operators';

/**
 * Collection invoicing proposal object returned by the API
 */
export class CollectionInvoicingProposalBasicInfo {
  Id: string;
  IwmId: number;
  Identifier: string;
  Name: string;
  Description: string;
  Type: string;
  Reference: string;
  Status: string;
  TargetDate: string;
  ReportingDate: string;
  SupplierId: string;
  SupplierName: string;
  SupplierStatus: string;
  SupplierStatusModifier: string;
  SupplierStatusModified: string;
  OrdererId: string;
  OrdererName: string;
  OrdererStatus: string;
  OrdererStatusModifier: string;
  OrdererStatusModified: string;
  TwoStepApprovalProcess: boolean;
  PartialInvoiceType: string;
  PartialInvoicePercentage: number;
  CheckerId: string;
  Archived: boolean;
  Created: string;
  LastModifiedTime: string;
  LastModifierPersonFullName: string;
}

@Injectable({
  providedIn: 'root'
})
export class CollectionInvoicingProposalBasicinfoService {

  private endPoint: string = this.config.backendIwmUri + 'InvoicingProposalApi/CollectionInvoicingProposals';

  constructor(private http: HttpClient, private config: AppConfig) { }

  /**
  * Get IP basic info.
  * @param id iwm id
  */
  public getCollectionInvoicingProposalBasicInfoById(iwmId: number): Observable<CollectionInvoicingProposalBasicInfo> {
    const url = `${this.endPoint}?$filter=IwmId eq ${iwmId}`;
    return this.http.get<CollectionInvoicingProposalBasicInfo>(url);
  }

  public getBasicinfo(cIpId: string): Observable<CollectionInvoicingProposalBasicInfo> {
    const url = `${this.endPoint}(${cIpId})`;
    return this.http.get<CollectionInvoicingProposalBasicInfo>(url);
  }

  public isStatusChangeAllowed(cIpId: string, newStatus: string = null): Observable<any> {
    const newstatusParam = newStatus ? `,newStatus='${newStatus}'` : '';
    const url = `${this.endPoint}/IsStatusChangeAllowed(collectionInvoicingProposalId=${cIpId}${newstatusParam})`;
    return this.http.get(url);
  }


  /**
   * Update collection invoicing proposal
   * @param {string} Id Id of the invoicing proposal
   * @param {CollectionInvoicingProposalBasicInfo} basicInfo InvoicingProposalBasicInfo object
   * @returns {Observable<boolean>}
   */
  public patch(cIpId: string, basicInfo: Partial<CollectionInvoicingProposalBasicInfo>): Observable<any> {
    const url = `${this.endPoint}(${cIpId})`;
    return this.http.patch(url, basicInfo);
  }

  /**
* Archive invoicing proposal (and catch possible error)
* @param {string} invoicingProposalId InvoicingProposalId
*/
  public archiveInvoicingProposal(invoicingProposalId: string): Observable<any> {
    const url = `${this.endPoint}(${invoicingProposalId})` + '/Default.Archive';
    const resp = this.http.post(url, {})
      .pipe(catchError(e => of(e)));
    return resp;
  }

  /**
  * Batch archive invoicing proposals
  * @param {Array<string>} ips array of invoicing proposal ids
  */
  public batchArchiveInvoicingProposals(ips: Array<string>): Observable<Array<string>> {
    const url = this.endPoint + '/Default.BatchArchive';
    return this.http.post<Array<string>>(url, { InvoicingProposals: ips })
      .pipe(map((x: any) => x?.value));
  }

  /**
  * Restore invoicing proposal (and catch possible error)
  * @param {string} invoicingProposalId InvoicingProposalId
  */
  public restoreInvoicingProposal(invoicingProposalId: string): Observable<any> {
    const url = `${this.endPoint}(${invoicingProposalId})` + '/Default.Restore';
    const resp = this.http.post(url, {})
      .pipe(catchError(e => of(e)));
    return resp;
  }

  /**
  * Batch restore invoicing proposals from archive
  * @param {Array<string>} ips array of invoicing proposal ids
  */
  public batchRestoreInvoicingProposals(ips: Array<string>): Observable<Array<string>> {
    const url = this.endPoint + '/Default.BatchRestore';
    return this.http.post<Array<string>>(url, { InvoicingProposals: ips })
      .pipe(map((x: any) => x?.value));
  }



  /**
  * Sets reporting date
  */
  public setReportingDate(cIpId: string, date: string): Observable<any> {
    const url = `${this.endPoint}(${cIpId})/Default.SetReportingDate`;
    return this.http.post(url, { ReportingDate: date });
  }

}
